import React from 'react';
import { useLocation } from 'react-router-dom';
import SeoHead from './SeoHead';
import { seoLandingPagePaths } from '../../pages/SeoLandingPages';
import { iosModuleLandingPagePaths } from '../../pages/IosModuleLandingPages';

const DEFAULT_KEYWORDS = 'Kundlistar, Nadi astrology, KP astrology, Jaimini astrology, divisional charts, birth chart calculator, Vedic astrology';

const RouteSeo: React.FC = () => {
  const location = useLocation();
  const { pathname } = location;

  const getConfig = () => {
    if (pathname === '/hindu-calendar' || pathname.startsWith('/hindu-calendar/')) return null;
    if (pathname === '/blog' || /^\/blog\//.test(pathname)) {
      return null;
    }

    if (/^\/chart\/[^/]+\/astro-logo$/.test(pathname)) {
      return null;
    }

    if (
      pathname === '/' ||
      pathname === '/start' ||
      pathname === '/install-iphone' ||
      pathname === '/kundlistar-studio' ||
      pathname === '/kundlistar-studio-vs-kundlisoft' ||
      pathname === '/features' ||
      pathname === '/kp-astrology-software' ||
      pathname === '/nadi-astrology-software' ||
      pathname === '/best-kp-astrology-software' ||
      pathname === '/vedic-kp-nadi-numerology-app' ||
      pathname === '/south-indian-kundli-chart-online' ||
      pathname === '/best-astrology-app-professional-astrologers-india' ||
      pathname === '/kundlistar-studio-vs-astrosage' ||
      pathname === '/kundlistar-studio-vs-jagannatha-hora' ||
      pathname === '/kundlistar-studio-vs-parashara-light' ||
      pathname === '/best-astrology-software-indian-astrologers' ||
      pathname === '/changelog' ||
      seoLandingPagePaths.includes(pathname) ||
      iosModuleLandingPagePaths.includes(pathname)
    ) {
      return null;
    }

    if (pathname === '/calculate') {
      return {
        title: 'Birth Chart Calculator | Kundlistar',
        description: 'Generate a precise Vedic birth chart with planets, houses, KP, Nadi, divisional charts, and dasha timelines.',
        keywords: 'birth chart calculator, kundli calculator, Vedic horoscope, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/horary') {
      return {
        title: 'Horary / Prashna Chart Calculator | Kundlistar',
        description: 'Create a horary or Prashna chart for question-based astrology analysis with professional-grade calculations.',
        keywords: 'horary astrology, prashna kundli, horary chart calculator, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/ruling-planets') {
      return {
        title: 'Ruling Planets Tool | Kundlistar',
        description: 'Calculate real-time ruling planets for KP and Nadi astrology timing, rectification, and event verification.',
        keywords: 'ruling planets, KP astrology, Nadi astrology timing, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/vedic-numerology') {
      return {
        title: 'Vedic Numerology Software | Moolank, Bhagyank & Name Correction | Kundlistar',
        description: 'Use Kundlistar Vedic numerology software to explore Moolank, Bhagyank, Surya Yantra grid, name correction, mobile number vibration, yogas and dasha timelines.',
        keywords: 'Vedic numerology software, Vedic numerology calculator, Moolank calculator, Bhagyank calculator, Surya Yantra grid, name correction calculator, mobile number numerology checker, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/lo-shu-grid-numerology') {
      return {
        title: 'Lo Shu Grid Numerology Calculator | Arrows, Missing Numbers & Mystical Triangle | Kundlistar',
        description: 'Calculate Lo Shu Grid numerology with the classic 4-9-2 magic square, missing numbers, repeated numbers, strength arrows, weakness arrows, yogs and mystical triangle insights.',
        keywords: 'Lo Shu Grid numerology, Lo Shu Grid calculator, missing numbers, arrows of strength, mystical triangle numerology, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/panchang') {
      return {
        title: 'Panchang Calculator | Kundlistar',
        description: 'Check tithi, nakshatra, yoga, karana, and weekday details with a clean Panchang interface.',
        keywords: 'panchang, tithi, nakshatra, yoga, karana, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/choghadiya') {
      return {
        title: 'Choghadiya Calculator | Kundlistar',
        description: 'Check current Choghadiya, day and night Choghadiya, Rahu Kaal flags, and personalised daily planning scores.',
        keywords: 'choghadiya, aaj ka choghadiya, rahu kaal, yamaganda, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/muhurat') {
      return {
        title: 'Muhurat Finder | Kundlistar',
        description: 'Find general and personalized Muhurat using Panchang, Choghadiya, Hora, Tarabala and Chandrabala.',
        keywords: 'muhurat, shubh muhurat, choghadiya, tarabala, chandrabala, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/varshphal') {
      return {
        title: 'Tajika Varshphal Annual Report | Kundlistar Studio',
        description: 'Generate a Tajika Varshphal annual report with Solar Return, Varsha Lagna, Muntha, Varsha Lord, Sahams and month-wise guidance.',
        keywords: 'Tajika Varshphal, annual horoscope, solar return, Muntha, Varsha Lagna, Kundlistar Studio',
        path: pathname,
        robots: 'noindex,follow',
      };
    }

    if (pathname === '/daily-horoscope' || /^\/daily-horoscope\//.test(pathname)) {
      return null;
    }

    if (pathname === '/matching') {
      return null;
    }

    if (pathname === '/watch-recommendation') {
      return null;
    }

    if (pathname === '/transit' || pathname === '/gochar-alerts') {
      return {
        title: 'Daily & Upcoming Gochar | Kundlistar',
        description: 'Follow today’s transit snapshot, upcoming gochar events, and premium chart-based transit impact inside Kundlistar.',
        keywords: 'gochar alerts, transit calendar, moon transit, planetary transit, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/current-transit') {
      return {
        title: 'Current Transit Analysis | Kundlistar',
        description: 'Track current planetary transits and review their astrological impact with live transit calculations.',
        keywords: 'current transit, gochar, planetary transit, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/pricing' || pathname === '/studio/pricing') {
      return {
        title: 'Pricing | Kundlistar',
        description: 'View Kundlistar Studio plans and features for astrologers, learners, and Pro users.',
        keywords: 'Kundlistar pricing, astrology software plans, subscription',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/about') {
      return {
        title: 'About Us | Kundlistar',
        description: 'Learn about Kundlistar, a professional astrology platform for Nadi, KP, BNN, Jaimini, Vedic charts, reports, and Jyotish workflows.',
        keywords: 'about Kundlistar, astrology software, Nadi astrology, KP astrology, Vedic astrology',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/contact') {
      return {
        title: 'Contact Us | Kundlistar',
        description: 'Contact Kundlistar for Android app support, plan access, kundli reports, subscriptions, privacy requests, and astrology platform enquiries.',
        keywords: 'contact Kundlistar, Kundlistar support, astrology app support, kundli app contact',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/predictions') {
      return {
        title: 'Predictions | Kundlistar',
        description: 'Explore astrology prediction features and analysis outputs available inside Kundlistar.',
        keywords: DEFAULT_KEYWORDS,
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/privacy-policy') {
      return {
        title: 'Privacy Policy | Kundlistar',
        description: 'Read how Kundlistar handles, protects, and stores user data and astrological information.',
        keywords: 'privacy policy, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/terms-of-service') {
      return {
        title: 'Terms of Service | Kundlistar',
        description: 'Review the terms and conditions for using Kundlistar and its astrology tools.',
        keywords: 'terms of service, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/disclaimer') {
      return {
        title: 'Disclaimer | Kundlistar',
        description: 'Read the legal and interpretive disclaimer for Kundlistar astrology tools and reports.',
        keywords: 'disclaimer, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/delete-account') {
      return {
        title: 'Delete Account | Kundlistar',
        description: 'Delete your Kundlistar account and understand what data is removed or retained.',
        keywords: 'delete account, Kundlistar',
        path: pathname,
        robots: 'index,follow',
      };
    }

    if (pathname === '/upgrade') {
      return {
        title: 'Upgrade Your Plan | Kundlistar',
        description: 'Upgrade your Kundlistar plan to unlock advanced astrology features and reports.',
        keywords: 'upgrade, subscription, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (pathname === '/dashboard') {
      return {
        title: 'Dashboard | Kundlistar',
        description: 'Manage charts, saved reports, and account activity inside your Kundlistar dashboard.',
        keywords: 'dashboard, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (pathname === '/profile') {
      return {
        title: 'Profile | Kundlistar',
        description: 'View and update your Kundlistar profile and account settings.',
        keywords: 'profile, account, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (pathname === '/login') {
      return {
        title: 'Login | Kundlistar',
        description: 'Sign in to Kundlistar to access saved charts, plan features, and your astrology workspace.',
        keywords: 'login, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (pathname === '/register') {
      return {
        title: 'Create Account | Kundlistar',
        description: 'Create your Kundlistar account to access charts, analysis pages, and astrology tools.',
        keywords: 'register, create account, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (pathname === '/forgot-password') {
      return {
        title: 'Forgot Password | Kundlistar',
        description: 'Reset your Kundlistar password securely.',
        keywords: 'forgot password, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (/^\/reset-password\//.test(pathname)) {
      return {
        title: 'Reset Password | Kundlistar',
        description: 'Choose a new password for your Kundlistar account.',
        keywords: 'reset password, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (pathname === '/admin' || pathname.startsWith('/admin/')) {
      return {
        title: 'Admin Panel | Kundlistar',
        description: 'Administrative tools for managing users, subscriptions, and charts.',
        keywords: 'admin, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (/^\/share\//.test(pathname)) {
      return {
        title: 'Shared Chart | Kundlistar',
        description: 'Open a shared astrology chart generated through Kundlistar.',
        keywords: 'shared chart, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (/^\/chart\/[^/]+\/nadi$/.test(pathname)) {
      return {
        title: 'Nadi Analysis | Kundlistar',
        description: 'Review Nadi astrology analysis for this chart including event themes and interpretive outputs.',
        keywords: 'nadi analysis, nadi astrology, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (/^\/chart\/[^/]+\/kp$/.test(pathname)) {
      return {
        title: 'KP Analysis | Kundlistar',
        description: 'Review KP astrology analysis, significators, cusps, and sub-lord interpretation for this chart.',
        keywords: 'KP analysis, KP astrology, sub lord, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (/^\/chart\/[^/]+\/divisional$/.test(pathname)) {
      return {
        title: 'Divisional Charts | Kundlistar',
        description: 'Open divisional charts, Ashtakavarga, Shadbala, Navtara, and dasha views for this chart.',
        keywords: 'divisional charts, varga charts, ashtakavarga, shadbala, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (/^\/chart\/[^/]+\/jaimini$/.test(pathname)) {
      return {
        title: 'Jaimini Analysis | Kundlistar',
        description: 'Open Jaimini analysis including Karakas, Char Dasha, Arudha charts, Karakamsha, and Upapada Lagna.',
        keywords: 'jaimini analysis, char dasha, arudha, karakamsha, upapada lagna, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (/^\/chart\/[^/]+\/rectification$/.test(pathname)) {
      return {
        title: 'Birth Time Rectification | Kundlistar',
        description: 'Use birth time rectification tools and event matching workflows for this chart.',
        keywords: 'birth time rectification, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (/^\/chart\/[^/]+\/bnn$/.test(pathname)) {
      return {
        title: 'BNN Analysis | Kundlistar',
        description: 'Open Bhrigu Nandi Nadi directional groups, string combinations, and transit activation analysis.',
        keywords: 'BNN analysis, bhrigu nandi nadi, Kundlistar',
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    if (/^\/chart\//.test(pathname)) {
      return {
        title: 'Chart Analysis | Kundlistar',
        description: 'Open chart analysis tools and astrology outputs for this saved chart.',
        keywords: DEFAULT_KEYWORDS,
        path: pathname,
        robots: 'noindex,nofollow',
      };
    }

    return {
      title: 'Kundlistar | Astrology Software',
      description: 'Professional astrology tools for chart calculation, analysis, dasha, and advanced Vedic workflows.',
      keywords: DEFAULT_KEYWORDS,
      path: pathname,
      robots: 'index,follow',
    };
  };

  const config = getConfig();
  if (!config) return null;

  return <SeoHead {...config} />;
};

export default RouteSeo;
